'use client';

/**
 * HardFreezeToggle — the platform HARD-FREEZE override, in the topbar.
 *
 * Distinct from the live master switch: turning live OFF still lets EXITS out
 * (reduce-only closes bypass a closed gate, so you can always get flat). HARD
 * FREEZE blocks EVERYTHING, including exits — for when even the close path is
 * untrusted (e.g. suspected bad market data). Enabling is a deliberate,
 * typed-confirmation action; disabling (the safe direction) is one click.
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Snowflake } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Textarea } from '@/components/ui/input';
import { getHardFreeze, hardFreezeQueryKey, setHardFreeze } from '@/lib/api/admin-platform';
import { cn } from '@/lib/util/cn';

export function HardFreezeToggle() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: hardFreezeQueryKey,
    queryFn: ({ signal }) => getHardFreeze(signal),
    refetchInterval: 20_000,
  });
  const frozen = q.data?.data.enabled ?? null;
  const [open, setOpen] = React.useState(false);

  const m = useMutation({
    mutationFn: (v: { enabled: boolean; reason: string }) => setHardFreeze(v.enabled, v.reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: hardFreezeQueryKey });
      setOpen(false);
    },
  });

  // Only render the control when frozen, or as a quiet affordance otherwise?
  // Keep it always visible so an operator can freeze fast; loud only when ON.
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Platform hard freeze (block ALL orders, including exits)"
        className={cn(
          'inline-flex items-center gap-1.5 rounded-sm border px-2 py-1',
          'text-2xs font-semibold transition-colors hover:brightness-110',
          frozen
            ? 'border-crit bg-crit text-bg'
            : 'border-border bg-surface-inset/60 text-fg-muted',
        )}
        aria-label="Hard freeze switch"
      >
        <Snowflake className="h-3 w-3 stroke-[1.6]" aria-hidden />
        <span>{frozen ? 'Frozen' : 'Freeze'}</span>
      </button>

      {open ? (
        <FreezeDialog
          currentlyFrozen={frozen === true}
          pending={m.isPending}
          error={m.error}
          onClose={() => { m.reset(); setOpen(false); }}
          onSubmit={(next, reason) => m.mutate({ enabled: next, reason })}
        />
      ) : null}
    </>
  );
}

function FreezeDialog({
  currentlyFrozen,
  pending,
  error,
  onClose,
  onSubmit,
}: {
  currentlyFrozen: boolean;
  pending: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (next: boolean, reason: string) => void;
}) {
  const next = !currentlyFrozen;
  const [reason, setReason] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const needsTyped = next === true; // enabling is the dangerous direction
  const canSubmit =
    reason.trim().length >= 4 &&
    (!needsTyped || confirm.trim().toUpperCase() === 'FREEZE') &&
    !pending;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) {onClose();} }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Snowflake className={cn('h-4 w-4', next ? 'text-crit' : 'text-fg-muted')} />
            {next ? 'Enable HARD FREEZE' : 'Lift hard freeze'}
          </DialogTitle>
          <DialogDescription>
            {next ? (
              <>
                Blocks <span className="font-mono text-crit">EVERY</span> live order —
                including reduce-only <span className="font-mono">EXITS</span> that the
                live switch still lets through. You will not be able to close positions
                via the engine until this is lifted. Use only when even closing is
                untrusted.
              </>
            ) : (
              <>Lifts the freeze. Orders return to the normal arm/live-switch gating.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {next ? (
            <div className="flex items-start gap-2 rounded border border-crit/50 bg-crit-soft px-3 py-2 text-2xs text-crit">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>This also blocks the panic-flatten path. To get flat, lift the freeze first.</span>
            </div>
          ) : null}
          <label className="block">
            <span className="text-2xs font-medium text-fg-subtle">Reason (audited)</span>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={next ? 'Why are you hard-freezing?' : 'Why are you lifting it?'}
              rows={2}
              autoFocus
            />
          </label>
          {needsTyped ? (
            <label className="block">
              <span className="text-2xs font-medium text-fg-subtle">
                Type <span className="font-mono text-crit">FREEZE</span> to confirm
              </span>
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="FREEZE" autoComplete="off" />
            </label>
          ) : null}
          {error ? (
            <div className="flex items-start gap-2 rounded border border-crit/50 bg-crit-soft px-3 py-2 text-2xs text-crit">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{error instanceof Error ? error.message : 'Request failed'}</span>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={pending}>Cancel</Button>
          <Button
            variant={next ? 'danger' : 'secondary'}
            size="sm"
            disabled={!canSubmit}
            onClick={() => onSubmit(next, reason.trim())}
          >
            {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {next ? 'Hard freeze' : 'Lift freeze'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
